'use client';

import React, { lazy, Suspense, useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import dynamic from 'next/dynamic';
import axios from 'axios';
import Body from '@/components/Body/Body';
import Spinner from 'react-bootstrap/Spinner';
import LazyMountOnVisible from '@/components/LazyMountOnVisible';
import FooterWidget from '@/components/FooterWidget';
import { HEADER_NAVBAR_ACTIVE_BG } from '@/utils/headerTheme';
import { agentSlugFromPath } from '@/utils/agentSlugFromPath';

const LoHomeClient = dynamic(() => import('@/components/LoHomeClient/LoHomeClient'));

const ForbsCarousel = lazy(() => import('@/components/ForbsCarousel/index'));
const Highlights = lazy(() => import('@/components/Highlights/Highlights'));
const VideoSection = lazy(() => import('@/components/VideoSection/ExploreSection'));
const NewsHighlights = lazy(() => import('@/components/Carousel/carousel'));
const ReelSection = lazy(() => import('@/components/ReelSection/reel'));
const FlowChart = lazy(() => import('@/components/Flowchart/FlowChart'));
const HowItWorks = lazy(() => import('@/components/HowItWorks/HowItWorks'));
const SignUpForm = lazy(() => import('@/components/SignUpForm/SignUpForm'));
const MediaCarousel = lazy(() =>
  import('media-logo-caraousel').then((mod) => ({ default: mod.MediaCarousel }))
);

const SpinnerFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
    <Spinner animation="border" role="status" style={{ width: '3rem', height: '3rem', borderWidth: '0.3em' }} />
  </div>
);

/**
 * `initial*` props are fetched at build time by app/page.js, so the hero copy,
 * headings and body text are already in the exported HTML -- a crawler that
 * doesn't run JS sees real content instead of a spinner. Seeding state from them
 * (rather than from null) is what removes the render gate; the effect below
 * re-fetches on mount and upgrades the data in place for real visitors.
 *
 * The sections below used to sit behind a `showOtherComponents` flag flipped by
 * a mount effect. Effects never run during `output: 'export'`, so every one of
 * them -- including the footer -- was permanently absent from the exported HTML.
 * They now render unconditionally. This deliberately gives back some of the
 * Total Blocking Time won in PAGESPEED.md: crawlable content was judged worth
 * more than the lab score. The one mount-gate kept is LazyMountOnVisible around
 * MediaCarousel, which is not a data gate -- that package runs a continuous rAF
 * loop for as long as it is mounted (PAGESPEED.md §3) and has no server-rendered
 * output to contribute anyway.
 */
export default function HomeClient({
  initialLinks,
  initialBanner,
  initialHighlights,
}) {
  const [bannerData, setBannerData] = useState(initialBanner);
  const [highlightsData, setHighlightsData] = useState(initialHighlights);
  const [links, setLinks] = useState(initialLinks);
  const [WorkflowKey, setWorkflowKey] = useState(null);
  const [agentSlug, setAgentSlug] = useState('');
  const pathname = usePathname();

  // CloudFront serves this page for /workshop/<agent>/, so render the agent from the URL.
  useEffect(() => {
    const slug = agentSlugFromPath(window.location.pathname);
    setAgentSlug(slug);
    if (!slug) document.getElementById('agent-route-style')?.remove();
  }, [pathname]);

  // Refresh branding + webinar data in the browser. Upgrade-only: on failure the
  // build-time seed is left in place rather than being replaced by a fallback,
  // since the seed already went through the same fallback chain at build time.
  useEffect(() => {
    const fetchData = async () => {
      const [linksRes, webinarRes] = await Promise.allSettled([
        axios.get(`${process.env.NEXT_PUBLIC_ANGELAI_JSON}?v=${Date.now()}`),
        axios.get(`${process.env.NEXT_PUBLIC_LIONJSON_CDN_URL}?v=${Date.now()}`),
      ]);
      if (linksRes.status === 'fulfilled' && linksRes.value.data?.current) {
        setLinks(linksRes.value.data);
      }
      if (webinarRes.status === 'fulfilled') {
        setBannerData({ ...initialBanner, ...(webinarRes.value.data.banner ?? {}) });
        setHighlightsData(webinarRes.value.data.highlights ?? initialHighlights);
      }
    };
    fetchData();
  }, [initialBanner, initialHighlights]);

  // Fetch workflow key
  useEffect(() => {
    const fetchWorkflowKey = async () => {
      try {
        const response = await axios.get(`${process.env.NEXT_PUBLIC_CDN_WORKFLOW_KEY}?v=${Date.now()}`);
        const fetchedWorkflowId = response.data.Workshop;
        const workflowKey = await axios.post(process.env.NEXT_PUBLIC_CDN_WORKFLOW_API, {
          workflow: fetchedWorkflowId,
        });
        setWorkflowKey(workflowKey.data.key);
      } catch (error) {
        console.error('Error fetching workflow key:', error);
      }
    };
    fetchWorkflowKey();
  }, []);

  return (
    <>
      {agentSlug ? (
        <LoHomeClient
          agentName={agentSlug}
          initialLoanOfficerData={null}
          initialLinks={links}
          showFooter={false}
        />
      ) : (
        <HomeBody
          links={links}
          bannerData={bannerData}
          highlightsData={highlightsData}
          WorkflowKey={WorkflowKey}
        />
      )}
      {/* One footer for both branches: the CDN footer loader only fills its mount once. */}
      <div id="global-footer" style={{ backgroundColor: HEADER_NAVBAR_ACTIVE_BG }} />
      <FooterWidget language="en" bgColor={HEADER_NAVBAR_ACTIVE_BG} />
    </>
  );
}

function HomeBody({ links, bannerData, highlightsData, WorkflowKey }) {
  return (
    <div className="bodyContainer home-body">
      <Body links={links} banner={bannerData} fetchpriority="high" />

      <div id="lazy-load-sentinel" style={{ height: 1 }} />

      <Suspense fallback={<SpinnerFallback />}>
        <ReelSection fetchpriority="low" />
        <FlowChart links={links.current} WorkflowKey={WorkflowKey} fetchpriority="low" />
        <ForbsCarousel fetchpriority="low" />
        <Highlights highlightsData={highlightsData} links={links.current} WorkflowKey={WorkflowKey} />
        <HowItWorks WorkflowKey={WorkflowKey} fetchpriority="low" />
        <LazyMountOnVisible fallback={<SpinnerFallback />}>
          <MediaCarousel fetchpriority="low" />
        </LazyMountOnVisible>
        <NewsHighlights fetchpriority="low" />
        {/*
          SignUpForm needs its OWN boundary, not the shared one above.
          It calls useSearchParams(), and on a statically rendered route Next
          bails that subtree out to client rendering by throwing up to the
          *nearest* Suspense boundary. Sharing the outer boundary would put the
          spinner -- not the sections -- in the exported HTML for the whole
          block, which is exactly what this change is trying to stop.
        */}
        <Suspense fallback={<SpinnerFallback />}>
          <SignUpForm fetchpriority="low" />
        </Suspense>
        <VideoSection links={links.current} fetchpriority="low" />
      </Suspense>
    </div>
  );
}
